import React, { useState } from 'react';
import { X, Phone, Check, Loader2, MessageSquare } from 'lucide-react';
import { Dialog, DialogContent } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import { submitInquiry, businessInfo } from '../mock';

export const BookingForm = ({ service, isOpen, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [inquiryRef, setInquiryRef] = useState('');
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    vehicleMake: '',
    vehicleModel: '',
    vehicleYear: '',
    registrationNumber: '',
    message: '',
    preferredDate: '',
    preferredTime: ''
  });

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const inquiry = await submitInquiry({
        ...formData,
        serviceId: service.id,
        serviceName: service.title
      });
      
      setInquiryRef(inquiry.id);
      setSubmitted(true);
      toast.success('Inquiry submitted! We\'ll call you back soon.');
    } catch (error) {
      toast.error('Failed to submit inquiry');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setSubmitted(false);
    setFormData({
      name: '',
      email: '',
      phone: '',
      vehicleMake: '',
      vehicleModel: '',
      vehicleYear: '',
      registrationNumber: '',
      message: '',
      preferredDate: '',
      preferredTime: ''
    });
    setInquiryRef('');
    onClose();
  };

  const handleEmergencyCall = () => {
    window.location.href = `tel:${businessInfo.phone}`;
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[600px] bg-slate-900 text-white border-slate-800 max-h-[90vh] overflow-y-auto">
        {!submitted ? (
          <>
            {/* Header */}
            <div className="flex items-start justify-between mb-6">
              <div>
                <h2 className="text-2xl font-bold text-white mb-1">
                  Request a Callback
                </h2>
                <p className="text-gray-400 text-sm">{service.title}</p>
              </div>
              <button
                onClick={resetForm}
                className="p-2 hover:bg-slate-800 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Emergency Call Banner */}
            <div className="bg-red-900/30 border border-red-700/50 rounded-xl p-4 mb-6">
              <div className="flex items-start space-x-3">
                <Phone className="w-5 h-5 text-red-400 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-red-300 font-semibold mb-2">Need Immediate Help?</p>
                  <p className="text-red-200 text-sm mb-3">
                    For emergencies or urgent assistance, call us directly now.
                  </p>
                  <button
                    onClick={handleEmergencyCall}
                    className="w-full flex items-center justify-center space-x-2 px-4 py-3 bg-red-600 hover:bg-red-700 text-white font-bold rounded-lg transition-all"
                  >
                    <Phone className="w-5 h-5" />
                    <span>Emergency Call: {businessInfo.phone}</span>
                  </button>
                </div>
              </div>
            </div>

            {/* Inquiry Form */}
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="name">Full Name *</Label>
                  <Input
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="bg-slate-800 border-slate-700"
                  />
                </div>
                <div>
                  <Label htmlFor="phone">Phone Number *</Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleInputChange}
                    required
                    className="bg-slate-800 border-slate-700"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="email">Email Address *</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  className="bg-slate-800 border-slate-700"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="vehicleMake">Vehicle Make</Label>
                  <Input
                    id="vehicleMake"
                    name="vehicleMake"
                    placeholder="e.g., BMW"
                    value={formData.vehicleMake}
                    onChange={handleInputChange}
                    className="bg-slate-800 border-slate-700"
                  />
                </div>
                <div>
                  <Label htmlFor="vehicleModel">Model</Label>
                  <Input
                    id="vehicleModel"
                    name="vehicleModel"
                    placeholder="e.g., M3"
                    value={formData.vehicleModel}
                    onChange={handleInputChange}
                    className="bg-slate-800 border-slate-700"
                  />
                </div>
                <div>
                  <Label htmlFor="vehicleYear">Year</Label>
                  <Input
                    id="vehicleYear"
                    name="vehicleYear"
                    placeholder="e.g., 2020"
                    value={formData.vehicleYear}
                    onChange={handleInputChange}
                    className="bg-slate-800 border-slate-700"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="registrationNumber">Registration Number</Label>
                <Input
                  id="registrationNumber"
                  name="registrationNumber"
                  placeholder="e.g., AB12 CDE"
                  value={formData.registrationNumber}
                  onChange={handleInputChange}
                  className="bg-slate-800 border-slate-700"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="preferredDate">Preferred Date</Label>
                  <Input
                    id="preferredDate"
                    name="preferredDate"
                    type="date"
                    value={formData.preferredDate}
                    onChange={handleInputChange}
                    className="bg-slate-800 border-slate-700"
                  />
                </div>
                <div>
                  <Label htmlFor="preferredTime">Preferred Time</Label>
                  <Input
                    id="preferredTime"
                    name="preferredTime"
                    type="time"
                    value={formData.preferredTime}
                    onChange={handleInputChange}
                    className="bg-slate-800 border-slate-700"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="message">Tell Us About Your Requirements *</Label>
                <Textarea
                  id="message"
                  name="message"
                  placeholder="Please describe what you need..."
                  value={formData.message}
                  onChange={handleInputChange}
                  rows={4}
                  required
                  className="bg-slate-800 border-slate-700"
                />
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-blue-600 hover:bg-blue-700"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <MessageSquare className="w-4 h-4 mr-2" />
                    Request Callback
                  </>
                )}
              </Button>

              <p className="text-xs text-gray-500 text-center">
                We'll review your request and call you back as soon as possible
              </p>
            </form>
          </>
        ) : (
          /* Success Message */
          <div className="space-y-6">
            <div className="flex items-start justify-between mb-4">
              <div className="flex-1"></div>
              <button
                onClick={resetForm}
                className="p-2 hover:bg-slate-800 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="text-center py-6">
              <div className="w-16 h-16 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <Check className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-2">Request Received!</h3>
              <p className="text-gray-400">
                We'll contact you shortly to discuss your requirements
              </p>
            </div>

            <div className="bg-slate-800 rounded-xl p-6 space-y-4 border border-slate-700">
              <div className="flex items-center justify-between pb-3 border-b border-slate-700">
                <span className="text-gray-400">Reference Number</span>
                <span className="font-mono font-bold text-blue-400">{inquiryRef}</span>
              </div>
              <div className="flex items-center justify-between pb-3 border-b border-slate-700">
                <span className="text-gray-400">Service</span>
                <span className="font-semibold text-white">{service.title}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-400">Contact Number</span>
                <span className="font-semibold text-white">{formData.phone}</span>
              </div>
            </div>

            <div className="bg-blue-900/30 border border-blue-700/50 rounded-xl p-4">
              <p className="text-sm text-blue-300">
                📧 A confirmation email has been sent to <strong>{formData.email}</strong>
              </p>
            </div>

            <div className="bg-red-900/30 border border-red-700/50 rounded-xl p-4">
              <p className="text-sm text-red-300 mb-3">
                <strong>Need immediate assistance?</strong>
              </p>
              <button
                onClick={handleEmergencyCall}
                className="w-full flex items-center justify-center space-x-2 px-4 py-3 bg-red-600 hover:bg-red-700 text-white font-bold rounded-lg transition-all"
              >
                <Phone className="w-5 h-5" />
                <span>Call {businessInfo.phone} Now</span>
              </button>
            </div>

            <Button
              onClick={resetForm}
              className="w-full bg-blue-600 hover:bg-blue-700"
            >
              Close
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
